import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/store/auth.store'

/**
 * Hook de solo lectura — la inicialización vive en AuthProvider (main.tsx).
 * No registra listeners: seguro de llamar en cualquier componente.
 */
export function useAuth() {
  const store = useAuthStore()

  const signIn = async (email: string, password: string) => {
    if (!supabase) throw new Error('Supabase no disponible')
    store.setLoading(true)
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) {
      store.setLoading(false)
      throw error
    }
    // AuthProvider (onAuthStateChange) actualizará el store y pondrá loading=false
  }

  const signOut = async () => {
    if (supabase) await supabase.auth.signOut()
    store.reset()
  }

  /**
   * Registra un usuario nuevo con rol "client".
   * El trigger handle_new_user() de Supabase crea el perfil automáticamente.
   * Devuelve needsEmailConfirmation=true si el proyecto exige confirmar el
   * correo antes de poder iniciar sesión (no llega sesión en la respuesta).
   */
  const signUp = async (email: string, password: string, username: string) => {
    if (!supabase) throw new Error('Supabase no disponible')
    store.setLoading(true)
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { username, role: 'client' } },
    })
    if (error) {
      store.setLoading(false)
      throw error
    }
    const needsEmailConfirmation = !data.session
    if (needsEmailConfirmation) store.setLoading(false)
    // Si sí llega sesión, AuthProvider (onAuthStateChange) actualiza el store.
    return { needsEmailConfirmation }
  }

  return {
    user: store.user,
    session: store.session,
    profile: store.profile,
    loading: store.loading,
    signIn,
    signUp,
    signOut,
  }
}
